import { useMutation, useQueryClient } from "react-query";
import {
  createComment,
  createPost,
  getOneComment,
  getPostEncodedID,
  uploadFiles,
} from "../../Helpers/backendHelper";
import { GET_ALL_POSTS } from "../posts/queryKey";
import {
  CommentResponse,
  MakeTradePostRequest,
} from "../../Helpers/backendResponseInterfaces";

/**
 * Custom hook for creating a new post.
 * @param {function} afterCreatePost Function to execute after post creation.
 * @param {number} page Page number to set after post creation.
 * @param {function} resetPosts Function to set the page number.
 * @returns {object} Mutation object from react-query.
 */
export const useCreatePostMutation = (
  afterCreatePost: (postId: number) => void,
  resetPosts?: () => void,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    any,
    any,
    {
      content?: string;
      tradeData?: MakeTradePostRequest;
      postType: string;
      parentPostId?: number;
    }
  >({
    mutationFn: ({ content, tradeData, postType, parentPostId }) =>
      createPost({
        content: content,
        trade_data: tradeData,
        post_type: postType,
        parent_post_id: parentPostId,
      }),
    onSuccess: (post) => {
      const data = post.data;
      if (data.post_id) {
        if (resetPosts) resetPosts();
        queryClient.invalidateQueries({ queryKey: [GET_ALL_POSTS, 1] });
        afterCreatePost(data.post_id);
      }
    },
    onError: (error) => {
      // Handle error scenarios here
      console.error("Error creating post:", error);
    },
  });

  return mutation;
};

/**
 * Custom hook for creating a new post.
 * @param {number} page Page number to set after post creation.
 * @param {function} resetValues Function to set the page number.
 * @param {function} resetPosts Function to set the page number.
 * @returns {object} Mutation object from react-query.
 */
export const useCreateTradePostMutation = (
  resetValues: () => void,
  resetPosts: () => void,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    any,
    any,
    { tradeData?: MakeTradePostRequest; postType: string }
  >({
    mutationFn: ({ tradeData, postType }) =>
      createPost({
        trade_data: tradeData,
        post_type: postType,
      }),
    onSuccess: (post) => {
      const data = post.data;
      if (data.post_id) {
        queryClient.invalidateQueries({ queryKey: [GET_ALL_POSTS, 1] });
        resetValues();
        resetPosts();
      }
    },
    onError: (error) => {
      // Handle error scenarios here
      console.error("Error creating post:", error);
    },
  });

  return mutation;
};

/**
 * Custom hook for uploading files associated with a post.
 * @param {function} resetValues Function to reset post creation values.
 * @returns {object} Mutation object from react-query.
 */
export const useUploadPostFilesMutation = (resetValues: () => void) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<any, any, { postId: number; files: FormData }>({
    mutationFn: ({ postId, files }) => uploadFiles(postId, files),
    onSuccess: (postUpload) => {
      const data = postUpload.data;
      if (data.ok) {
        queryClient.invalidateQueries({ queryKey: [GET_ALL_POSTS] });
        resetValues();
      }
    },
    onError: (error) => {
      // Handle error scenarios here
      console.error("Error uploading post files:", error);
    },
  });

  return mutation;
};

/**
 * Custom hook for creating a new post.
 * @param {function} setComments Function to execute after post creation.
 * @returns {object} Mutation object from react-query.
 */
export const useGetOneCommentMutation = (
  setComments: (comment: any) => void,
) => {
  useQueryClient();
  const mutation = useMutation<any, any, { commentId: number }>({
    mutationFn: ({ commentId }) => getOneComment(commentId),
    onSuccess: (post: { data: CommentResponse }) => {
      const comment = post.data;
      if (comment) {
        setComments((prevComments: CommentResponse[]) => [
          comment,
          ...prevComments,
        ]);
      }
    },
    onError: (error) => {
      // Handle error scenarios here
      console.error("Error getting coment:", error);
    },
  });

  return mutation;
};

/**
 * Custom hook for creating a new post.
 * @param {function} afterCreateComment Function to execute after post creation.
 * @returns {object} Mutation object from react-query.
 */
export const useCreateCommentMutation = (
  afterCreateComment: (commentId: number) => void,
) => {
  useQueryClient();
  const mutation = useMutation<any, any, { content: string; postId: number }>({
    mutationFn: ({ content, postId }) =>
      createComment({ content: content, post_id: postId }),
    onSuccess: (post) => {
      const comment = post.data;
      if (comment.comment_id) {
        afterCreateComment(comment.comment_id);
      }
    },
    onError: (error) => {
      // Handle error scenarios here
      console.error("Error creating comment:", error);
    },
  });

  return mutation;
};

/**
 * Custom hook for copying post link.
 * @param {function} afterCopyPostID Function to execute after post creation.
 * @returns {object} Mutation object from react-query.
 */
export const useCopyPostLinkMutation = (
  afterCopyPostID: (postId: string) => void,
) => {
  const mutation = useMutation<any, any, { postId: number }>({
    mutationFn: ({ postId }) => getPostEncodedID(postId),
    onSuccess: (data) => {
      afterCopyPostID(data.data.post_id);
    },
    onError: (error) => {
      // Handle error scenarios here
      console.error("Error copying post link:", error);
    },
  });

  return mutation;
};
